import { Component, Input } from '@angular/core';
import { ComponentBase } from '../_component.base.js';
import { LogoSize } from '../../constants/LogoSize.js';
import { Company, Constants } from 'company-finder-common';
import { DeploymentContext } from '../../utilities/deployment-context/deployment-context.js';

@Component({
	selector: 'company-logo',
	templateUrl: './company-logo.component.html',
	styleUrls: ['./company-logo.component.scss'],
	standalone: false,
})
export class CompanyLogoComponent extends ComponentBase {
	@Input()
	public company: Company;
	@Input()
	public logoBase64: string;
	@Input()
	public show?: boolean = undefined;
	@Input()
	public size: LogoSize = LogoSize.ExtraLarge;

	constructor(dc: DeploymentContext) {
		super(dc);
	}

	public get logoClass(): string {
		switch (this.size) {
			case LogoSize.Small:
				return 'cf_company-logo-small';
			case LogoSize.SmallPlus:
				return 'cf_company-logo-small-plus';
			case LogoSize.Medium:
				return 'cf_company-logo-medium';
			case LogoSize.Large:
				return 'cf_company-logo-large';
			case LogoSize.ExtraLarge:
				return 'cf_company-logo';
		}
		return 'unknown';
	}

	public get backgroundUrlStyle() {
		if (this.logoBase64) {
			return {
				'background-image': `url(data:image/gif;base64,${this.logoBase64})`,
			};
		}

		// We don't have a company it this is company self update, and if none was ever provided
		// we also don't have a logoBase64, so fetch the placeholder logo
		if (!this.company?.logoBase64Hash) {
			return {};
		}

		// Allow for global caching of the logos for improved performance.
		// The hash of the logo ensures client side invalidation if the logo changes.
		return { 'background-image': `url(${this.logoUrl})` };
	}

	// Given that there is extra processing cost with the secure image,
	// only load the image this way if we need auth headers to load it.
	public get loadSecureImg(): boolean {
		// While implementing ADJQ-1888 it was noticed that these values are coming in
		// as 0 or 1 rather than boolean values. It did not seem worth the impact of
		// fixing that, so check for undefined
		const isStealthMode = this.definedOrDefault(
			this.company?.isStealthMode,
			false
		);
		const isPublicityAgreementSigned = this.definedOrDefault(
			this.company?.isPublicityAgreementSigned,
			true
		);

		return (
			this.company?.logoBase64Hash && // this only matters if the company actually has a logo (can't secure load the placeholder image)
			(!isPublicityAgreementSigned || isStealthMode)
		);
	}

	public definedOrDefault(obj, def: boolean) {
		return obj === null || obj === undefined ? def : obj;
	}

	public get logoUrl(): string {
		return `/api/v1/company/logo/${this.companyIdWithCacheKey}`;
	}

	public get companyIdWithCacheKey(): string {
		return this.company
			? `${encodeURIComponent(this.company.id)}?${
					Constants.PARAM_CACHEABLE
				}=true&${this.company.logoBase64Hash}`
			: ``;
	}
}
